/**
 * utils/configItemLifecycle.ts — FRONTEND MIRROR of supabase/functions/server/configItemLifecycleCore.ts.
 *
 * Generated: do not edit by hand; run scripts/sync-discovery-grid-mirror.sh.
 * tests/server/configItemLifecycle_test.ts asserts parity.
 */


export const STATUSES = ['discovery', 'ready_for_build', 'in_build', 'ready_for_review', 'approved', 'blocked'] as const;
export type LifecycleStatus = (typeof STATUSES)[number];

export type Court = 'client' | 'consultant' | null;

const LABELS: Record<LifecycleStatus, string> = {
  discovery: 'Discovery',
  ready_for_build: 'Ready for build',
  in_build: 'In build',
  ready_for_review: 'Ready for review',
  approved: 'Approved',
  blocked: 'Blocked',
};

/** Legacy spellings of `status`, folded (decision record §3). Anything else reads as discovery. */
const STATUS_FOLD: Record<string, LifecycleStatus> = {
  discovery: 'discovery',
  not_started: 'discovery',
  'not-started': 'discovery',
  in_progress: 'discovery',
  draft: 'discovery',
  waiting_client_input: 'discovery',
  waiting_consultant_response: 'discovery',
  waiting_consultant_review: 'discovery',
  waiting_consultant_action: 'discovery',
  review: 'discovery',
  pending: 'discovery',
  ready_for_build: 'ready_for_build',
  'ready-to-build': 'ready_for_build',
  'ready-for-build': 'ready_for_build',
  ready: 'ready_for_build',
  locked: 'ready_for_build',
  in_build: 'in_build',
  building: 'in_build',
  'in-progress': 'in_build',
  ready_for_test: 'in_build',
  in_test: 'in_build',
  ready_for_review: 'ready_for_review',
  'ready-for-review': 'ready_for_review',
  'in-review': 'ready_for_review',
  approved: 'approved',
  done: 'approved',
  completed: 'approved',
  implemented: 'approved',
  built: 'approved',
  blocked: 'blocked',
  'on-hold': 'blocked',
  on_hold: 'blocked',
};

const LOCKED: ReadonlySet<LifecycleStatus> = new Set<LifecycleStatus>(['ready_for_build', 'in_build', 'ready_for_review', 'approved']);
const RANK: Record<LifecycleStatus, number> = { discovery: 0, ready_for_build: 1, in_build: 2, ready_for_review: 3, approved: 4, blocked: -1 };

export function isLifecycleStatus(value: unknown): value is LifecycleStatus {
  return typeof value === 'string' && (STATUSES as readonly string[]).includes(value);
}

/** One stored `status` value (any spelling) read as a lifecycle status. */
export function foldStatus(status: unknown): LifecycleStatus {
  if (typeof status !== 'string') return 'discovery';
  return STATUS_FOLD[status.trim()] ?? 'discovery';
}

/**
 * The lifecycle status of an item as stored today: `status` folded, then refined by the legacy
 * `implementationStatus` when one is present (it was the more specific build fact).
 */
export function normalizeStatus(item: { status?: unknown; implementationStatus?: unknown } | string | null | undefined): LifecycleStatus {
  if (typeof item === 'string') return foldStatus(item);
  if (!item || typeof item !== 'object') return 'discovery';
  const status = foldStatus(item.status);
  const legacy = typeof item.implementationStatus === 'string' ? item.implementationStatus.trim() : '';
  if (!legacy || status === 'blocked') return status;
  const further = (to: LifecycleStatus) => (RANK[to] > RANK[status] ? to : status);
  switch (legacy) {
    case 'in-progress':
      return status; // a client "configuring" an unlocked item is discovery; a locked one already reads ready_for_build or further
    case 'ready-for-build':
    case 'ready':
      return further('ready_for_build');
    case 'ready-for-review':
      return further('ready_for_review');
    case 'approved':
    case 'implemented':
      return 'approved';
    default:
      return status;
  }
}

/** Locked for build: ready_for_build and everything after it (the grid's level 5). Accepts any spelling. */
export function isLocked(status: unknown): boolean {
  return LOCKED.has(foldStatus(status));
}

/** Whose court a status puts the item in; `discovery` keeps whatever the discovery sub-flows stored. */
export function courtFor(status: LifecycleStatus, stored?: unknown): Court {
  switch (status) {
    case 'ready_for_build':
    case 'in_build':
    case 'blocked':
      return 'consultant';
    case 'ready_for_review':
      return 'client';
    case 'approved':
      return null;
    default:
      return stored === 'client' || stored === 'consultant' ? stored : 'client';
  }
}

export function labelFor(status: unknown): string {
  return LABELS[foldStatus(status)];
}

export interface Move {
  to: LifecycleStatus;
  requiresComment: boolean;
}

export interface MoveOptions {
  /** A member of the consulting firm (or a system admin). */
  firm: boolean;
  /** Where a blocked item returns to on unblock; read from the item's `blockedFrom`. */
  blockedFrom?: unknown;
}

/**
 * The moves open to the actor from `from` (decision record §1). Unlock (back to `discovery`) is
 * never among them: the unlock route alone does that, with a reason and the change-order choice.
 */
export function allowedMoves(from: LifecycleStatus, opts: MoveOptions): Move[] {
  const moves: Move[] = [];
  if (from === 'blocked') {
    if (opts.firm) {
      const back = foldStatus(opts.blockedFrom);
      moves.push({ to: back === 'blocked' ? 'discovery' : back, requiresComment: false });
    }
    return moves;
  }
  if (from === 'discovery') moves.push({ to: 'ready_for_build', requiresComment: false });
  if (opts.firm) {
    if (from === 'ready_for_build') moves.push({ to: 'in_build', requiresComment: false });
    if (from === 'in_build') {
      moves.push({ to: 'ready_for_review', requiresComment: false });
      moves.push({ to: 'ready_for_build', requiresComment: false });
    }
    if (from === 'ready_for_review') {
      moves.push({ to: 'approved', requiresComment: true });
      moves.push({ to: 'in_build', requiresComment: true });
    }
    moves.push({ to: 'blocked', requiresComment: false });
  } else if (from === 'ready_for_review') {
    moves.push({ to: 'approved', requiresComment: false });
    moves.push({ to: 'in_build', requiresComment: true });
  }
  return moves;
}

export interface MoveActor {
  userId: string;
  userEmail?: string;
  userName?: string;
  role?: string;
  /** A member of the consulting firm (or a system admin). */
  firm: boolean;
}

export interface ApplyMoveInput {
  comment?: string;
  actor: MoveActor;
  now?: string;
}

export interface MoveResult {
  ok: boolean;
  error?: string;
  from?: LifecycleStatus;
  item?: Record<string, any>;
}

/**
 * Apply a move: the new item, or why not. Never mutates `item`. Writes `status` (canonical),
 * `statusHistory`, `ballInCourt`, `lockedAt`/`lockedBy` on the lock, `implementedAt` on approve,
 * `blockedFrom` on block; drops the legacy `implementationStatus` and `locked` fields.
 */
export function applyMove(item: Record<string, any>, to: unknown, input: ApplyMoveInput): MoveResult {
  const now = input.now ?? new Date().toISOString();
  const target = typeof to === 'string' && STATUS_FOLD[to.trim()] ? STATUS_FOLD[to.trim()] : null;
  if (!target) return { ok: false, error: `Unknown status: ${String(to)}` };
  const from = normalizeStatus(item);
  if (target === 'discovery') return { ok: false, from, error: 'Reopening discovery is the unlock route (a reason and the change-order choice are required)' };
  const move = allowedMoves(from, { firm: input.actor.firm, blockedFrom: item.blockedFrom }).find((m) => m.to === target);
  if (!move) return { ok: false, from, error: `Cannot move this item from ${labelFor(from).toLowerCase()} to ${labelFor(target).toLowerCase()}` };
  const text = (input.comment ?? '').trim();
  if (move.requiresComment && !text) return { ok: false, from, error: 'A comment is required for this move' };

  const { userId, userName, userEmail, role } = input.actor;
  const entry = {
    fromStatus: from,
    toStatus: target,
    changedBy: { userId, userName: userName || userEmail || userId, userEmail: userEmail || '', role: role || (input.actor.firm ? 'consultant' : 'user') },
    changedAt: now,
    timestamp: now,
    comment: text || undefined,
    automated: false,
  };
  const { implementationStatus: _legacyBuild, locked: _legacyLock, ...rest } = item;
  const next: Record<string, any> = {
    ...rest,
    status: target,
    statusHistory: [...(Array.isArray(item.statusHistory) ? item.statusHistory : []), entry],
    ballInCourt: courtFor(target, item.ballInCourt),
    statusUpdatedAt: now,
    statusUpdatedBy: userId,
    lastModified: now,
    lastModifiedBy: userId,
  };
  if (target === 'ready_for_build' && from === 'discovery') {
    next.lockedAt = now;
    next.lockedBy = { userId, userName: userName || userEmail || userId, userEmail: userEmail || '' };
    next.lockedReason = text || 'Configuration settled; locked for build';
  }
  if (target === 'approved') {
    next.implementedAt = now;
    next.implementedBy = { userId, userEmail: userEmail || '', userName };
    next.approvedBy = userId;
    next.completedAt = now;
    next.completedBy = userId;
  }
  if (target === 'blocked') {
    next.blockedFrom = from;
    next.blockedAt = now;
    if (text) next.blockerReason = text;
  } else if (from === 'blocked') {
    delete next.blockedFrom;
    delete next.blockedAt;
    delete next.blockerReason;
  }
  return { ok: true, from, item: next };
}
